import React, { useState, useEffect } from 'react';
import { DollarSign, Users, ArrowUpRight, ArrowDownRight, Send, Gift, RefreshCw, Settings, Server, Cloud, AlertTriangle } from 'lucide-react';
import * as billingService from '../services/billingService';
import * as orchestratorService from '../services/orchestratorService';

const AdminBillingPage = () => {
  const [revenue, setRevenue] = useState(null);
  const [balances, setBalances] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [stripeBalance, setStripeBalance] = useState(null);
  const [payouts, setPayouts] = useState([]);
  const [cloudCosts, setCloudCosts] = useState(null);
  const [billingSettings, setBillingSettings] = useState(null);
  const [clusterCosts, setClusterCosts] = useState([]);
  const [awsCredits, setAwsCredits] = useState(null);
  // Vast/RunPod account balances. Only providers enabled on the orchestrator
  // are reported, so an unconfigured provider is absent rather than erroring.
  const [providerBalances, setProviderBalances] = useState([]);
  const [loading, setLoading] = useState(true);
  const [txPage, setTxPage] = useState(0);
  const [grantForm, setGrantForm] = useState({ user_id: '', amount: '', description: '' });
  const [payoutForm, setPayoutForm] = useState({ amount: '' });
  const [granting, setGranting] = useState(false);
  const [payingOut, setPayingOut] = useState(false);
  const [savingSettings, setSavingSettings] = useState(false);
  const [settingsForm, setSettingsForm] = useState({
    global_markup_percent: '20',
    auto_stop_enabled: 'true',
    aws_credit_grant_cents: '',
    aws_credit_start_date: '',
    aws_credit_expires_at: '',
  });

  useEffect(() => { fetchAll(); }, []);
  useEffect(() => { fetchTransactions(); }, [txPage]);

  const fetchAll = async () => {
    try {
      const [revRes, balRes, stripeRes, payoutsRes, costRes, settingsRes, clusterCostsRes, awsCreditsRes, provBalRes] = await Promise.allSettled([
        billingService.adminGetRevenueSummary(),
        billingService.adminGetAllBalances(),
        billingService.adminGetStripeBalance(),
        billingService.adminListPayouts(),
        orchestratorService.getCostReport(),
        billingService.adminGetSettings(),
        billingService.adminGetClusterCosts(),
        billingService.adminGetAwsCredits(),
        billingService.adminGetProviderBalances(),
      ]);
      if (revRes.status === 'fulfilled' && revRes.value.success) setRevenue(revRes.value);
      if (balRes.status === 'fulfilled' && balRes.value.success) setBalances(balRes.value.balances || []);
      if (stripeRes.status === 'fulfilled' && stripeRes.value.success) setStripeBalance(stripeRes.value.balance);
      if (payoutsRes.status === 'fulfilled' && payoutsRes.value.success) setPayouts(payoutsRes.value.payouts || []);
      if (costRes.status === 'fulfilled' && costRes.value.success) setCloudCosts(costRes.value);
      if (settingsRes.status === 'fulfilled' && settingsRes.value.success) {
        const s = settingsRes.value.settings || {};
        setBillingSettings(s);
        setSettingsForm({
          global_markup_percent: s.global_markup_percent || '20',
          auto_stop_enabled: s.auto_stop_enabled || 'true',
          // Stored in cents; the form edits whole dollars.
          aws_credit_grant_cents: s.aws_credit_grant_cents
            ? String(parseInt(s.aws_credit_grant_cents, 10) / 100)
            : '',
          aws_credit_start_date: s.aws_credit_start_date || '',
          aws_credit_expires_at: s.aws_credit_expires_at || '',
        });
      }
      if (clusterCostsRes.status === 'fulfilled' && clusterCostsRes.value.success) setClusterCosts(clusterCostsRes.value.costs || []);
      if (awsCreditsRes.status === 'fulfilled' && awsCreditsRes.value.success) setAwsCredits(awsCreditsRes.value.credits);
      if (provBalRes.status === 'fulfilled' && provBalRes.value.success) setProviderBalances(provBalRes.value.balances || []);
    } catch (err) {
      console.error('Admin fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchTransactions = async () => {
    try {
      const res = await billingService.adminGetAllTransactions({ limit: 50, offset: txPage * 50 });
      if (res.success) setTransactions(res.transactions || []);
    } catch (err) {
      console.error('Failed to fetch admin transactions:', err);
    }
  };

  const handleGrant = async (e) => {
    e.preventDefault();
    setGranting(true);
    try {
      const res = await billingService.adminGrantCredits(
        parseInt(grantForm.user_id),
        parseInt(grantForm.amount),
        grantForm.description
      );
      if (res.success) {
        setGrantForm({ user_id: '', amount: '', description: '' });
        await fetchAll();
        alert('Credits granted successfully');
      } else {
        alert(res.error || 'Failed to grant credits');
      }
    } catch (err) {
      alert('Failed to grant credits');
    } finally {
      setGranting(false);
    }
  };

  const handlePayout = async (e) => {
    e.preventDefault();
    if (!window.confirm(`Create payout of $${(parseInt(payoutForm.amount) / 100).toFixed(2)}?`)) return;
    setPayingOut(true);
    try {
      const res = await billingService.adminCreatePayout(parseInt(payoutForm.amount));
      if (res.success) {
        setPayoutForm({ amount: '' });
        await fetchAll();
        alert('Payout created successfully');
      } else {
        alert(res.error || 'Failed to create payout');
      }
    } catch (err) {
      alert('Failed to create payout');
    } finally {
      setPayingOut(false);
    }
  };

  const handleSaveSettings = async (e) => {
    e.preventDefault();
    setSavingSettings(true);
    try {
      const res = await billingService.adminUpdateSettings({
        global_markup_percent: parseFloat(settingsForm.global_markup_percent),
        auto_stop_enabled: settingsForm.auto_stop_enabled,
        // Entered in dollars, persisted in cents to match every other money
        // value in billing_settings. Empty clears the grant.
        aws_credit_grant_cents: settingsForm.aws_credit_grant_cents
          ? Math.round(parseFloat(settingsForm.aws_credit_grant_cents) * 100)
          : '',
        aws_credit_start_date: settingsForm.aws_credit_start_date,
        aws_credit_expires_at: settingsForm.aws_credit_expires_at,
      });
      if (res.success) {
        await fetchAll();
        alert('Billing settings saved');
      } else {
        alert(res.error || 'Failed to save settings');
      }
    } catch (err) {
      alert('Failed to save settings');
    } finally {
      setSavingSettings(false);
    }
  };

  const formatCents = (c) => `$${((c || 0) / 100).toFixed(2)}`;
  const formatDate = (d) => d ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : '-';

  if (loading) {
    return <div className="text-center py-12 text-gray-500">Loading admin billing data...</div>;
  }

  const stripeAvailable = stripeBalance?.available?.[0]?.amount || 0;
  const stripePending = stripeBalance?.pending?.[0]?.amount || 0;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-gray-900">Admin Billing</h2>
        <button onClick={fetchAll} className="text-gray-500 hover:text-gray-700 p-2">
          <RefreshCw size={18} />
        </button>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-4">
        <div className="bg-white p-5 rounded-lg shadow border">
          <p className="text-xs text-gray-500 font-medium">Stripe Balance</p>
          <p className="text-2xl font-bold text-blue-600">{formatCents(stripeAvailable)}</p>
          {stripePending > 0 && (
            <p className="text-xs text-gray-400 mt-1">+{formatCents(stripePending)} pending</p>
          )}
        </div>
        <div className="bg-white p-5 rounded-lg shadow border">
          <p className="text-xs text-gray-500 font-medium">Total Purchases</p>
          <p className="text-2xl font-bold text-green-600">{formatCents(revenue?.total_purchased_cents)}</p>
          <p className="text-xs text-gray-400 mt-1">
            Before Stripe fees
          </p>
        </div>
        <div className="bg-white p-5 rounded-lg shadow border">
          <p className="text-xs text-gray-500 font-medium">Cluster Debits</p>
          <p className="text-2xl font-bold text-orange-500">{formatCents(revenue?.total_cluster_debits_cents)}</p>
        </div>
        <div className="bg-white p-5 rounded-lg shadow border">
          <p className="text-xs text-gray-500 font-medium">Infra Cost</p>
          <p className="text-2xl font-bold text-red-600">{formatCents(revenue?.total_infra_cost_cents)}</p>
          <p className="text-xs text-gray-400 mt-1">
            Margin {formatCents(revenue?.total_compute_margin_cents)}
          </p>
        </div>
        <div className="bg-white p-5 rounded-lg shadow border">
          <p className="text-xs text-gray-500 font-medium">Net Revenue</p>
          <p className="text-2xl font-bold text-gray-900">{formatCents(revenue?.net_revenue_cents)}</p>
          <p className="text-xs text-gray-400 mt-1">
            Purchases − refunds − infra
          </p>
        </div>
        <div className="bg-white p-5 rounded-lg shadow border">
          <p className="text-xs text-gray-500 font-medium">Inference Debits</p>
          <p className="text-2xl font-bold text-red-500">{formatCents(revenue?.total_inference_debits_cents)}</p>
          {revenue?.inference && (
            <p className="text-xs text-gray-400 mt-1">{revenue.inference.total_calls} calls, avg {Math.round(revenue.inference.avg_latency_ms)}ms</p>
          )}
        </div>
      </div>

      {/* Cloud costs (from orchestrator) */}
      {cloudCosts && (
        <div className="bg-white p-5 rounded-lg shadow border">
          <h3 className="font-semibold text-gray-900 mb-2">Cloud Costs (Provider)</h3>
          <p className="text-sm text-gray-600">
            Total: {formatCents((cloudCosts.total_cost || 0) * 100)} |
            Active clusters: {cloudCosts.active_clusters || 0} |
            {revenue && ` Margin: ${formatCents(revenue.net_revenue_cents - (cloudCosts.total_cost || 0) * 100)}`}
          </p>
        </div>
      )}

      {/* AWS promotional credit.
          AWS publishes no remaining-balance API, so this is the recorded grant
          minus the credit Cost Explorer reports as applied since the start date. */}
      {providerBalances.length > 0 && (
        <div className="bg-white p-5 rounded-lg shadow border">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-semibold text-gray-900 flex items-center space-x-2">
              <Cloud size={18} />
              <span>Provider Balances</span>
            </h3>
            <span className="text-xs text-gray-400">enabled providers only</span>
          </div>

          {/* A depleted provider account evicts running clusters, and the
              provider gives no useful signal — Vast just reports "exited". This
              is the number that explains it, so a low balance is called out. */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {providerBalances.map((b) => {
              const low = typeof b.balance === 'number' && b.balance < 10;
              return (
                <div
                  key={b.provider}
                  className={`rounded-lg border p-3 ${
                    b.error ? 'border-gray-200 bg-gray-50'
                      : low ? 'border-amber-300 bg-amber-50' : 'border-gray-200'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-gray-700 capitalize">{b.provider}</span>
                    {low && !b.error && (
                      <span className="text-xs font-semibold text-amber-700">LOW</span>
                    )}
                  </div>
                  {b.error ? (
                    <p className="text-xs text-gray-500 mt-1">Unavailable: {b.error}</p>
                  ) : (
                    <>
                      <div className={`text-2xl font-bold mt-1 ${low ? 'text-amber-700' : 'text-gray-900'}`}>
                        ${b.balance.toFixed(2)}
                      </div>
                      {typeof b.spend_per_hour === 'number' && b.spend_per_hour > 0 && (
                        <p className="text-xs text-gray-500 mt-0.5">
                          spending ${b.spend_per_hour.toFixed(3)}/h
                          {b.balance > 0 && ` · ~${Math.floor(b.balance / b.spend_per_hour)}h left`}
                        </p>
                      )}
                      {low && (
                        <p className="text-xs text-amber-700 mt-1">
                          Running clusters are evicted when this reaches zero.
                        </p>
                      )}
                    </>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {awsCredits && (
        <div className="bg-white p-5 rounded-lg shadow border">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-semibold text-gray-900 flex items-center space-x-2">
              <Cloud size={18} />
              <span>AWS Credit</span>
            </h3>
            {awsCredits.as_of && (
              <span className="text-xs text-gray-400">
                as of {formatDate(awsCredits.as_of)}{awsCredits.cached ? ' · cached' : ''}
              </span>
            )}
          </div>

          {awsCredits.reason ? (
            <div className="flex items-start space-x-2 text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded p-3">
              <AlertTriangle size={16} className="mt-0.5 shrink-0" />
              <div>
                <p className="font-medium">Credit data unavailable</p>
                <p className="text-xs text-amber-600 mt-1">{awsCredits.reason}</p>
              </div>
            </div>
          ) : (
            <>
              {awsCredits.configured ? (
                <>
                  <div className="flex items-baseline space-x-3">
                    <p className="text-3xl font-bold text-gray-900">
                      {formatCents(awsCredits.remaining_cents)}
                    </p>
                    <p className="text-sm text-gray-500">
                      remaining of {formatCents(awsCredits.grant_cents)}
                    </p>
                  </div>

                  <div className="w-full bg-gray-200 rounded-full h-2 mt-3">
                    <div
                      className={`h-2 rounded-full ${
                        awsCredits.percent_remaining < 20 ? 'bg-red-500'
                          : awsCredits.percent_remaining < 50 ? 'bg-amber-500'
                          : 'bg-green-500'
                      }`}
                      style={{ width: `${Math.max(0, Math.min(100, awsCredits.percent_remaining))}%` }}
                    />
                  </div>

                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4 text-sm">
                    <div>
                      <p className="text-xs text-gray-500">Used to date</p>
                      <p className="font-medium text-gray-900">{formatCents(awsCredits.applied_cents)}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">Burn (30d)</p>
                      <p className="font-medium text-gray-900">{formatCents(awsCredits.burn_30d_cents)}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">Projected dry</p>
                      <p className="font-medium text-gray-900">
                        {awsCredits.projected_dry_date || '—'}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">Expires</p>
                      <p className={`font-medium ${
                        awsCredits.days_until_expiry != null && awsCredits.days_until_expiry < 30
                          ? 'text-red-600' : 'text-gray-900'
                      }`}>
                        {awsCredits.expires_at || '—'}
                        {awsCredits.days_until_expiry != null && (
                          <span className="text-xs text-gray-400 ml-1">
                            ({awsCredits.days_until_expiry}d)
                          </span>
                        )}
                      </p>
                    </div>
                  </div>
                </>
              ) : (
                <p className="text-sm text-gray-600">
                  No grant recorded. Enter the total from the AWS Billing console's
                  Credits page below — AWS has no API for it, so the balance can
                  only be derived once the grant is known.
                </p>
              )}

              {awsCredits.monthly?.length > 0 && (
                <div className="mt-4 pt-3 border-t">
                  <p className="text-xs text-gray-500 mb-2">Credit applied per month</p>
                  <div className="flex flex-wrap gap-x-5 gap-y-1 text-xs">
                    {awsCredits.monthly.slice(0, 8).map((m) => (
                      <span key={m.month} className="text-gray-600">
                        {m.month} <span className="font-medium text-gray-900">{formatCents(m.cents)}</span>
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* Billing Settings */}
      <div className="bg-white p-5 rounded-lg shadow border">
        <h3 className="font-semibold text-gray-900 mb-3 flex items-center space-x-2">
          <Settings size={18} />
          <span>Billing Settings</span>
        </h3>
        <form onSubmit={handleSaveSettings} className="flex flex-wrap items-end gap-4">
          <div>
            <label className="block text-xs text-gray-500 mb-1">Global Markup %</label>
            <input
              type="number" step="0.1" min="0" max="500"
              value={settingsForm.global_markup_percent}
              onChange={(e) => setSettingsForm(p => ({ ...p, global_markup_percent: e.target.value }))}
              className="border rounded px-3 py-2 text-sm w-28"
            />
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">Auto-stop on low balance</label>
            <select
              value={settingsForm.auto_stop_enabled}
              onChange={(e) => setSettingsForm(p => ({ ...p, auto_stop_enabled: e.target.value }))}
              className="border rounded px-3 py-2 text-sm"
            >
              <option value="true">Enabled</option>
              <option value="false">Disabled</option>
            </select>
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">AWS Credit Grant ($)</label>
            <input
              type="number" step="0.01" min="0"
              placeholder="e.g. 5000"
              value={settingsForm.aws_credit_grant_cents}
              onChange={(e) => setSettingsForm(p => ({ ...p, aws_credit_grant_cents: e.target.value }))}
              className="border rounded px-3 py-2 text-sm w-32"
            />
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">Credit Start</label>
            <input
              type="date"
              value={settingsForm.aws_credit_start_date}
              onChange={(e) => setSettingsForm(p => ({ ...p, aws_credit_start_date: e.target.value }))}
              className="border rounded px-3 py-2 text-sm"
            />
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">Credit Expires</label>
            <input
              type="date"
              value={settingsForm.aws_credit_expires_at}
              onChange={(e) => setSettingsForm(p => ({ ...p, aws_credit_expires_at: e.target.value }))}
              className="border rounded px-3 py-2 text-sm"
            />
          </div>
          <button type="submit" disabled={savingSettings} className="bg-purple-600 text-white px-4 py-2 rounded-lg text-sm hover:bg-purple-700 disabled:opacity-50">
            {savingSettings ? 'Saving...' : 'Save Settings'}
          </button>
        </form>
        <p className="text-xs text-gray-400 mt-2">
          Markup is applied on top of cloud provider cost for clusters without a per-model rate. Auto-stop halts clusters when user balance {"<"} 1 hour of cost.
        </p>
        <p className="text-xs text-gray-400 mt-1">
          AWS publishes no credit-balance API, so the grant total and its window are
          recorded here by hand (from the Billing console's Credits page). Consumption
          is read back automatically from Cost Explorer. Set the start date to when the
          current grant became active so earlier, expired pools are excluded.
        </p>
      </div>

      {/* Cluster Costs */}
      {clusterCosts.length > 0 && (
        <div className="bg-white rounded-lg shadow border">
          <div className="p-4 border-b">
            <h3 className="font-semibold text-gray-900 flex items-center space-x-2">
              <Server size={18} />
              <span>Cluster Billing ({clusterCosts.length})</span>
            </h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="text-left px-4 py-2 text-xs text-gray-500">Cluster</th>
                  <th className="text-left px-4 py-2 text-xs text-gray-500">Owner</th>
                  <th className="text-right px-4 py-2 text-xs text-gray-500">Cloud Cost</th>
                  <th className="text-right px-4 py-2 text-xs text-gray-500">Markup</th>
                  <th className="text-right px-4 py-2 text-xs text-gray-500">Total Charged</th>
                  <th className="text-right px-4 py-2 text-xs text-gray-500">Periods</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {clusterCosts.map(c => (
                  <tr key={c.cluster_id} className="hover:bg-gray-50">
                    <td className="px-4 py-2 font-medium">{c.cluster_name}</td>
                    <td className="px-4 py-2 text-gray-500">#{c.user_id}</td>
                    <td className="px-4 py-2 text-right font-mono text-gray-600">{formatCents(c.total_cloud_cost_cents)}</td>
                    <td className="px-4 py-2 text-right font-mono text-gray-600">{formatCents(c.total_markup_cents)}</td>
                    <td className="px-4 py-2 text-right font-mono font-semibold text-orange-600">{formatCents(c.total_charged_cents)}</td>
                    <td className="px-4 py-2 text-right text-gray-500">{c.billing_periods}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Grant credits */}
        <div className="bg-white p-5 rounded-lg shadow border">
          <h3 className="font-semibold text-gray-900 mb-3 flex items-center space-x-2">
            <Gift size={18} />
            <span>Grant Credits</span>
          </h3>
          <form onSubmit={handleGrant} className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <input
                type="number" placeholder="User ID" required
                value={grantForm.user_id}
                onChange={(e) => setGrantForm(p => ({ ...p, user_id: e.target.value }))}
                className="border rounded px-3 py-2 text-sm"
              />
              <input
                type="number" placeholder="Credits (cents)" required
                value={grantForm.amount}
                onChange={(e) => setGrantForm(p => ({ ...p, amount: e.target.value }))}
                className="border rounded px-3 py-2 text-sm"
              />
            </div>
            <input
              type="text" placeholder="Description (optional)"
              value={grantForm.description}
              onChange={(e) => setGrantForm(p => ({ ...p, description: e.target.value }))}
              className="w-full border rounded px-3 py-2 text-sm"
            />
            <button type="submit" disabled={granting} className="bg-purple-600 text-white px-4 py-2 rounded-lg text-sm hover:bg-purple-700 disabled:opacity-50">
              {granting ? 'Granting...' : 'Grant Credits'}
            </button>
          </form>
        </div>

        {/* Payout */}
        <div className="bg-white p-5 rounded-lg shadow border">
          <h3 className="font-semibold text-gray-900 mb-3 flex items-center space-x-2">
            <Send size={18} />
            <span>Create Payout</span>
          </h3>
          <form onSubmit={handlePayout} className="space-y-3">
            <input
              type="number" placeholder="Amount (cents)" required
              value={payoutForm.amount}
              onChange={(e) => setPayoutForm({ amount: e.target.value })}
              className="w-full border rounded px-3 py-2 text-sm"
            />
            <p className="text-xs text-gray-500">Available: {formatCents(stripeAvailable)}</p>
            <button type="submit" disabled={payingOut} className="bg-blue-600 text-white px-4 py-2 rounded-lg text-sm hover:bg-blue-700 disabled:opacity-50">
              {payingOut ? 'Processing...' : 'Send Payout'}
            </button>
          </form>

          {payouts.length > 0 && (
            <div className="mt-4">
              <p className="text-xs font-medium text-gray-500 mb-2">Recent Payouts</p>
              <div className="space-y-1">
                {payouts.slice(0, 5).map(p => (
                  <div key={p.id} className="flex justify-between text-xs text-gray-600">
                    <span>{formatCents(p.amount)} ({p.status})</span>
                    <span>{formatDate(p.created * 1000)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* User balances */}
      <div className="bg-white rounded-lg shadow border">
        <div className="p-4 border-b">
          <h3 className="font-semibold text-gray-900 flex items-center space-x-2">
            <Users size={18} />
            <span>User Balances ({balances.length})</span>
          </h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="text-left px-4 py-2 text-xs text-gray-500">User</th>
                <th className="text-right px-4 py-2 text-xs text-gray-500">Balance</th>
                <th className="text-right px-4 py-2 text-xs text-gray-500">Last Updated</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {balances.map(b => (
                <tr key={b.user_id} className="hover:bg-gray-50">
                  <td className="px-4 py-2">
                    <span className="font-medium">{b.username || b.email}</span>
                    <span className="text-gray-400 ml-2">#{b.user_id}</span>
                  </td>
                  <td className="px-4 py-2 text-right font-mono">
                    <span className={b.balance_cents > 0 ? 'text-green-600' : 'text-gray-400'}>
                      {formatCents(b.balance_cents)}
                    </span>
                  </td>
                  <td className="px-4 py-2 text-right text-gray-500 text-xs">{formatDate(b.updated_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* All transactions */}
      <div className="bg-white rounded-lg shadow border">
        <div className="p-4 border-b">
          <h3 className="font-semibold text-gray-900">All Transactions</h3>
        </div>
        <div className="divide-y">
          {transactions.map(tx => (
            <div key={tx.id} className="px-4 py-3 flex items-center justify-between hover:bg-gray-50">
              <div className="flex items-center space-x-3">
                {tx.amount_cents > 0
                  ? <ArrowUpRight size={16} className="text-green-500" />
                  : <ArrowDownRight size={16} className="text-red-500" />
                }
                <div>
                  <p className="text-sm font-medium">{tx.username || tx.email} <span className="text-gray-400">#{tx.user_id}</span></p>
                  <p className="text-xs text-gray-500">{tx.description || tx.type} - {formatDate(tx.created_at)}</p>
                </div>
              </div>
              <span className={`text-sm font-semibold ${tx.amount_cents > 0 ? 'text-green-600' : 'text-red-600'}`}>
                {tx.amount_cents > 0 ? '+' : ''}{tx.amount_cents}
              </span>
            </div>
          ))}
        </div>
        {transactions.length >= 50 && (
          <div className="p-3 border-t flex justify-between">
            <button onClick={() => setTxPage(p => Math.max(0, p - 1))} disabled={txPage === 0} className="text-sm text-purple-600 disabled:text-gray-400">Previous</button>
            <button onClick={() => setTxPage(p => p + 1)} className="text-sm text-purple-600">Next</button>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminBillingPage;
